import { z } from "zod"

import { accountAnalysisSummarySchema } from "./account-analysis-summary"
import { calculatedCostSchema } from "./calculated-cost"
import { scenarioSchema } from "./scenario"
import { seriesSchema } from "./series"
import { seriesMeasureSchema } from "./series-measure"

export const accountAnalysisSchema = z.object({
  /** This is null for Account Analysis (so ignore it).
   * It is used in Project Analysis (different endpoint). */
  designId: z.string().nullable().optional(),
  /** Represents the status of the underlying data
   * (profiles) that the analysis ran on. 2 means
   * all the underlying data is up to date. 1 means
   * it’s still processing, and you should run the
   * analysis again once the profile is done
   * (this is very unusual). */
  dataStatus: z.number(),
  /** Each scenario in the analysis. For a solar PV
   * analysis there will be a “before” for without
   * solar electric bill, “after” for the with solar
   * electric bill, “solar” for the solar system, and
   * “savings” for net savings with solar
   * (before - after + solar). See below for the data
   * structure of the Scenario object. */
  scenarios: z.array(scenarioSchema),
  /** A list of Series objects, each one describing
   * the data points (SeriesMeasure) it contains.
   * The Series object is documented in further detail
   * below. */
  series: z.array(seriesSchema),
  /** This list holds the actual data points, each of
   * type SeriesMeasure (described below). Each ties to a
   * particular series (above) using a unique integer
   * value. Each SeriesMeasure holds a rate, quantity,
   * and cost. */
  seriesData: z.array(seriesMeasureSchema),
  /** If populateCosts is set to true, this field is
   * populated with CalculatedCost objects corresponding
   * to each series. They are indexed by seriesId,
   * which you can find from the series collection. */
  seriesCosts: z.union([
    z
      .record(
        z.enum([`1`, `2`, `3`, `4`, `5`, `6`, `7`, `8`, `9`, `10`]),
        calculatedCostSchema,
      )
      .optional(),
    z.array(calculatedCostSchema).optional(),
  ]),

  summary: accountAnalysisSummarySchema,
})

export type AccountAnalysis = z.infer<typeof accountAnalysisSchema>
